import {
  ArrowRightCircle,
  BarChart3,
  Briefcase,
  CheckSquare,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleMinus,
  CirclePlay,
  CircleX,
  ClipboardCheck,
  Clock,
  FileText,
  Funnel,
  LayoutDashboard,
  Mail,
  MessagesSquare,
  Network,
  Pause,
  Settings,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { SlaState } from '@nest-recruit/shared';
import styles from './Tone.module.css';

// Tones (ADR 0035). Nine status tones, one per meaning — every status in the app maps to
// one of them (`status-tones.ts`) — and one tone per area of the app. None is grey
// (DECISIONS-LOG 2026-10-06 "A colour for every status, no grey anywhere").
export type StatusTone = 'urgent' | 'attention' | 'new' | 'progress' | 'live' | 'done' | 'hold' | 'rejected' | 'ended';
export type AreaTone =
  | 'dashboard'
  | 'organisation'
  | 'requisition'
  | 'job'
  | 'screening'
  | 'assessment'
  | 'interview'
  | 'offer'
  | 'tasks'
  | 'reports'
  | 'admin';
export type Tone = StatusTone | AreaTone;

const TONE_ICONS: Readonly<Record<Tone, LucideIcon>> = {
  urgent: CircleAlert,
  attention: Clock,
  new: CircleDashed,
  progress: ArrowRightCircle,
  live: CirclePlay,
  done: CircleCheck,
  hold: Pause,
  rejected: CircleX,
  ended: CircleMinus,
  dashboard: LayoutDashboard,
  organisation: Network,
  requisition: FileText,
  job: Briefcase,
  screening: Funnel,
  assessment: ClipboardCheck,
  interview: MessagesSquare,
  offer: Mail,
  tasks: CheckSquare,
  reports: BarChart3,
  admin: Settings,
};

/** Solid chips are kept for what needs someone, so they stand out of a long list. */
const SOLID_TONES: ReadonlySet<Tone> = new Set<Tone>(['urgent', 'attention']);

/** Sets `--tone` from `data-tone` without styling anything, for an element that uses the colour its own way. */
export const toneClass: string = styles.tone ?? '';

/** The classes for a tone card (fading outline and corner flag); pair with `data-tone={tone}`. */
export function toneCardClass(isInteractive = false): string {
  return [styles.tone, styles.card, isInteractive ? styles.interactive : ''].filter(Boolean).join(' ');
}

/** A breached clock is urgent, a warning one needs action; otherwise the item keeps its own tone. */
export function slaTone(state: SlaState, otherwise: Tone): Tone {
  if (state === 'breached') return 'urgent';
  if (state === 'warning') return 'attention';
  return otherwise;
}

/** Urgent and needs-action chips are solid with white ink and their icon; every other tone is a light tint. */
export function ToneChip({ tone, children }: { readonly tone: Tone; readonly children: ReactNode }): ReactNode {
  const isSolid = SOLID_TONES.has(tone);
  const Icon = TONE_ICONS[tone];
  return (
    <span className={`${styles.tone} ${styles.chip} ${isSolid ? styles.solid : styles.tint}`} data-tone={tone}>
      {isSolid && <Icon size={12} aria-hidden />}
      {children}
    </span>
  );
}

/**
 * The tone's icon on a tinted tile. As a tone card's first child it sits in the card's
 * top-left corner; anywhere else (`size="sm"` in a table row or tree) it sits in line.
 * `icon` replaces the tone's own icon — an area's tile shows the area's icon in its tone.
 */
export function ToneIcon({
  tone,
  icon,
  size = 'md',
}: {
  readonly tone: Tone;
  readonly icon?: LucideIcon;
  readonly size?: 'sm' | 'md';
}): ReactNode {
  const Icon = icon ?? TONE_ICONS[tone];
  return (
    <span className={[styles.tone, styles.icon, size === 'sm' ? styles.iconSm : ''].filter(Boolean).join(' ')} data-tone={tone} aria-hidden>
      <Icon aria-hidden />
    </span>
  );
}
